import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Camera barcode/QR scanner (WP-03.4) for the screens that already accept a scanned code by keyboard wedge.
 *
 * The label-control and till screens were built around a USB scanner gun, which types into a focused input and is
 * still the fastest option at a fixed workstation — this does not replace it. It exists for the phone/tablet case
 * (a supervisor walking the line, a store without a gun), so it is opt-in behind a button and hands the decoded
 * text to the same `onScan` the typed path uses.
 *
 * `html5-qrcode` is loaded on demand: it is ~400 kB and most sessions never open a camera, so importing it at the
 * top of the bundle would cost every page load for a feature used on a few screens. */
export function BarcodeScanner({ onScan, onClose }: { onScan: (text: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const regionId = useId().replace(/:/g, "");
  const scannerRef = useRef<{ stop: () => Promise<void>; clear: () => void } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let instance: { stop: () => Promise<void>; clear: () => void } | null = null;

    void (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const scanner = new Html5Qrcode(regionId);
        instance = scanner as unknown as { stop: () => Promise<void>; clear: () => void };
        scannerRef.current = instance;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded: string) => {
            // Stop before handing the code over: the caller usually submits straight away, and a second frame
            // decoding the same label would fire the verification twice.
            void scanner.stop().then(() => onScan(decoded)).catch(() => onScan(decoded));
          },
          () => { /* per-frame decode misses are normal, not errors */ },
        );
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
      const s = instance ?? scannerRef.current;
      if (s) void s.stop().then(() => s.clear()).catch(() => { /* already stopped */ });
    };
  }, [regionId, onScan]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-sm font-semibold">{t("common.scanTitle")}</h2>
          <button onClick={onClose} className="text-xs text-brand-grey hover:text-brand-primary">{t("common.close")}</button>
        </div>
        <div id={regionId} className="mt-3 overflow-hidden rounded border border-brand-grey-light" />
        {error
          ? <p className="mt-2 text-[11px] text-status-red">{t("common.scanError")} — {error}</p>
          : <p className="mt-2 text-[11px] text-brand-grey">{t("common.scanHint")}</p>}
      </div>
    </div>
  );
}
